import { React, useCallback, useState, useTranslation, Waiting, observer, main } from '../..';
import { Form, SelectHierarchy } from '@weezevent/nacre';
import { RatesTree } from '../../../../../models';
import QuotasModel from '../../../../../models/quotasModel';
import { useMemo } from 'react';

const now = new Date().toISOString();

const RatesSelect = observer(() => {
    const { event, organization } = main;
    let { selectedRates, selectedWeezDateId, selectedSessionsRates, selectedSessionsRateGroups, hasSelectedSessions } = main;
    const [tree, setTree] = useState(null);
    const [indexes, setIndexes] = useState({});
    const { t } = useTranslation();

    const internalValue = React.useMemo(() => {
        return selectedRates.length > 0 && selectedRates[0] ? selectedRates[0].id : null;
    }, [selectedRates]);

    const options = useMemo(() => {
        const optionsList = tree?.toOptions({
            filter: rate => {
                let isActive = false;
                if (!rate.visibility_end_date || rate.visibility_end_date === null || rate.visibility_end_date > now) {
                    isActive = true;
                } else if (rate.visibility_end_date && rate.visibility_end_date < now) {
                    isActive = false;
                }
                if (hasSelectedSessions && !selectedSessionsRates.includes(rate.pk)) {
                    return null;
                }
                return (
                    (rate.distributor === null || rate.distributor === undefined) &&
                    Object.keys(indexes)
                        .map(rate_id => parseInt(rate_id))
                        .includes(rate.pk) &&
                    isActive
                );
            },
            filterGroup: gr => {
                if (hasSelectedSessions && !selectedSessionsRateGroups.includes(gr.pk)) {
                    return null;
                }
                return gr.removed === null && (gr.children.length || gr.rates.length);
            }
        });
        const list = tree?.rates_list; // we look at rates_list and not toOptions() to avoid groups

        if (tree && list?.length === 1) {
            const onlyRate = optionsList[0]?.value;
            main.setSelectedRates([indexes[onlyRate]]);
        }

        return optionsList;
    }, [tree, main, indexes, selectedSessionsRates, selectedSessionsRateGroups, hasSelectedSessions]);

    const [quotas] = QuotasModel.useApiModel({
        organizationId: organization.pk,
        eventId: event.pk,
        getForRates: true
    });

    RatesTree.useApiModel(
        {
            organizationId: organization.pk,
            eventId: event.pk,
            onSuccess: tree => {
                const indexes = {};
                const sessionSelectedId = selectedWeezDateId || 0;

                tree.eachRate?.(rate => {
                    indexes[rate.id] = rate;
                    let rate_quota = null;
                    let quota = quotas?.find(qt => qt.rate_id === rate.id && qt.session_id === sessionSelectedId);
                    if (quota) {
                        rate_quota = quota.quota_total ? quota.quota_total - quota.quota_taken : null;
                    }
                    let quota_group = null;
                    if (rate.group) {
                        let quota_grp = quotas?.find(qt => qt.rate_id === 0 && qt.rate_group_id === rate.group && qt.session_id === sessionSelectedId);
                        if (quota_grp) {
                            quota_group = quota_grp.quota_total ? quota_grp.quota_total - quota_grp.quota_taken : null;
                        }
                    }

                    let quota_event = null;
                    let quota_evt = quotas?.find(qt => qt.rate_id === 0 && qt.event_id === event.pk && qt.session_id === sessionSelectedId);
                    if (quota_evt) {
                        quota_event = quota_evt.quota_total ? quota_evt.quota_total - quota_evt.quota_taken : null;
                    }
                    rate.setQuotas({ event: quota_event, group: quota_group, rate: rate_quota });
                    rate.price = 0;
                });
                setTree(tree);
                main.setTree(tree);
                setIndexes(indexes);
            }
        },
        [quotas, selectedWeezDateId]
    );

    const handleChange = useCallback(
        _selected => {
            if (!Array.isArray(_selected)) {
                _selected = [_selected];
            }
            const rates = _selected.filter(id => indexes[id]).map(id => indexes[id]);
            main.setSelectedRates(rates);
        },
        [indexes]
    );

    if (!tree && !options) {
        return <Waiting />;
    }

    return (
        <Form.Field>
            <Form.Label label={t('attendees.invitations.stepChoices.label')} />
            <SelectHierarchy
                placeholder={t('ticket.common.action.select')}
                placeholderSearch={t('ticket.common.action.search')}
                depth={0}
                height="maxi"
                value={internalValue}
                onChange={handleChange}
                options={options}
                multi={false}
                widthIndent={'10px'}
            />
        </Form.Field>
    );
});

export default RatesSelect;
